import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import {
  createContract,
  deleteContract,
  updateContract,
  type ContractWriteRequest,
} from "@/features/contracts/api/contracts.api"
import { contractsKeys } from "@/features/contracts/hooks/use-contracts"
import { extractApiErrorMessage } from "@/lib/api/client"

export function useCreateContract() {
  const { t } = useTranslation("contracts")
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createContract,
    onSuccess: () => {
      toast.success(t("toast.createdTitle"))
      queryClient.invalidateQueries({ queryKey: contractsKeys.list() })
    },
    onError: (error) => {
      toast.error(extractApiErrorMessage(error))
    },
  })
}

export function useUpdateContract(contractId: string) {
  const { t } = useTranslation("contracts")
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: ContractWriteRequest) =>
      updateContract(contractId, payload),
    onSuccess: () => {
      toast.success(t("toast.updatedTitle"))
      queryClient.invalidateQueries({ queryKey: contractsKeys.list() })
      queryClient.invalidateQueries({
        queryKey: contractsKeys.detail(contractId),
      })
    },
    onError: (error) => {
      toast.error(extractApiErrorMessage(error))
    },
  })
}

export function useDeleteContract() {
  const { t } = useTranslation("contracts")
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (contractId: string) => deleteContract(contractId),
    onSuccess: (_data, contractId) => {
      toast.success(t("toast.deletedTitle"))
      queryClient.invalidateQueries({ queryKey: contractsKeys.list() })
      queryClient.removeQueries({
        queryKey: contractsKeys.detail(contractId),
      })
    },
    onError: (error) => {
      toast.error(extractApiErrorMessage(error))
    },
  })
}
